import { Button, DatePicker, Form, Input, InputNumber, Select } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardFilterList from './DashboardFilterList/DashboardFilterList';
import { getBulkUploadStatus, MandateBulkUploadFilter } from 'api/nach';
import { SelectResponse } from 'api/nach';
import { Option } from 'antd/lib/mentions';
import moment from 'moment';
import { handleOnlyNumber } from 'features/nach/utils/handleOnlyNumber';

type Props = {
  isLoading: boolean;
  onSubmit: (values: MandateBulkUploadFilter) => Promise<void>;
  reloadByTabChange: boolean;
};

const bulkUploadFilter: MandateBulkUploadFilter = {};

const initSelectResp: SelectResponse = {
  data: [],
};

const dateFormat = 'DD/MM/YYYY';

export default function Index({ onSubmit, reloadByTabChange }: Props) {
  const [filter, setFilter] = useState(bulkUploadFilter);
  const [status, setStatus] = useState(initSelectResp);
  const [isOpen, setIsOpen] = useState(false);
  const [onlyDigitError, setOnlyDigitError] = useState(false);
  const isAdmin = sessionStorage.getItem('isAdmin');

  useEffect(() => {
    getBulkUploadStatus().then((res) => {
      setStatus(res);
    });
  }, []);

  const [form] = Form.useForm<any>();

  const handleSubmit = useCallback(async () => {
    const currentValues = form.getFieldsValue();
    if (currentValues.fromDate) {
      currentValues.fromDate = moment(currentValues.fromDate).format('YYYY-MM-DD') + ' 00:00:00';
    }
    if (currentValues.toDate) {
      currentValues.toDate = moment(currentValues.toDate).format('YYYY-MM-DD') + ' 23:59:59';
    }

    setFilter(currentValues);
  }, [onSubmit, form, filter]);

  const handleReset = (_e: any) => {
    form.resetFields();
    setFilter(bulkUploadFilter);
    setOnlyDigitError(false);
  };

  const filterClicked = (_r: any) => {
    setIsOpen(!isOpen);
  };

  const memoizedFilter = useMemo(() => filter, [filter]);

  const handleFromDateChange = (value: moment.Moment | null) => {
    form.setFieldsValue({ fromDate: value });
    form.validateFields(['fromDate', 'toDate']);
  };

  const handleToDateChange = (value: moment.Moment | null) => {
    form.setFieldsValue({ toDate: value });
    form.validateFields(['fromDate', 'toDate']);
  };

  const validateFromDate = (_: any, value: moment.Moment) => {
    const toDate = form.getFieldValue('toDate');
    if (toDate && value && value.isAfter(toDate)) {
      return Promise.reject(new Error('From date cannot be after To date'));
    }
    return Promise.resolve();
  };

  const validateToDate = (_: any, value: moment.Moment) => {
    const fromDate = form.getFieldValue('fromDate');
    if (fromDate && value && value.isBefore(fromDate)) {
      return Promise.reject(new Error('To date cannot be before From date'));
    }
    return Promise.resolve();
  };

  // Disable dates in the To Date picker that are before the selected From Date
  const disableToDate = (current: moment.Moment) => {
    const fromDate = form.getFieldValue('fromDate');
    return current && fromDate && current.isBefore(fromDate, 'day');
  };

  // Disable dates in both pickers that are after today's date
  const disableFutureDates = (current: moment.Moment) => {
    return current && current.isAfter(moment(), 'day');
  };

  return (
    <div>
      <div className="row">
        <div className="col-xs-12 col-sm-12 col-md-12">
          <Button
            shape="circle"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
                <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z"></path>
              </svg>
            }
            onClick={filterClicked}
            style={{ float: 'right', margin: 10, zIndex: 1000, border: 0 }}
          />
        </div>
      </div>

      <div className="submission-htmlForm">
        {isOpen ? (
          <Form<MandateBulkUploadFilter>
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            scrollToFirstError
            className="standard-forms"
            initialValues={{
              ['Username']: sessionStorage.getItem('user')?.toString(),
              ['SourceApplicationName']:
                isAdmin != 'true' ? sessionStorage.getItem('source')?.toString() : null,
            }}
          >
            <div>
              <div className="row">
                <div className="col-xs-12 col-sm-4 col-md-4">
                  <div className="input-box">
                    <Form.Item
                      label={'File Id :'}
                      name="bulkUploadFileId"
                      className="fieldTitle"
                      validateStatus={onlyDigitError ? 'error' : ''}
                      help={onlyDigitError ? 'Only numbers are allowed' : ''}
                    >
                      <InputNumber
                        onBeforeInput={(e) => handleOnlyNumber(e, setOnlyDigitError)}
                        placeholder={'File Id:'}
                      />
                    </Form.Item>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-4 col-md-4">
                  <div className="input-box">
                    <Form.Item label={'File Name :'} name="fileName" className="fieldTitle">
                      <Input placeholder={'File Name'} />
                    </Form.Item>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-4 col-md-4">
                  <div className="input-box">
                    <Form.Item label={'Request Status :'} name="status" className="fieldTitle">
                      <Select size="large" placeholder="Select">
                        {status.data.map((status) => (
                          <Option value={status.status}>{status.status}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-xs-12 col-sm-4 col-md-4">
                  <div className="input-box">
                    <Form.Item
                      label={'From :'}
                      name="fromDate"
                      className="fieldTitle"
                      rules={[{ validator: validateFromDate }]}
                    >
                      <DatePicker
                        format={dateFormat}
                        onChange={handleFromDateChange}
                        disabledDate={disableFutureDates} // Disable future dates
                      />
                    </Form.Item>
                  </div>
                </div>

                <div className="col-xs-12 col-sm-4 col-md-4">
                  <div className="input-box">
                    <Form.Item
                      label={'To :'}
                      name="toDate"
                      className="fieldTitle"
                      rules={[{ validator: validateToDate }]}
                    >
                      <DatePicker
                        format={dateFormat}
                        onChange={handleToDateChange}
                        disabledDate={(current) =>
                          disableToDate(current) || disableFutureDates(current)
                        } // Disable dates before From Date and future dates
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-xs-12 col-sm-4 col-md-12">
                  <div className="input-box">
                    <div style={{ marginTop: '10px', float: 'right' }}>
                      <Button
                        type="primary"
                        onClick={handleReset}
                        style={{ float: 'right', marginRight: '5px' }}
                      >
                        {'CLEAR'}
                      </Button>
                      &nbsp; &nbsp;
                      <Button
                        type="primary"
                        htmlType="submit"
                        style={{ float: 'right', marginRight: '10px' }}
                      >
                        {'SEARCH'}
                      </Button>
                      &nbsp; &nbsp;
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        ) : null}
      </div>

      <DashboardFilterList
        MandateBulkUploadFilter={memoizedFilter}
        setOnlyDigitError={setOnlyDigitError}
        onSubmit={handleSubmit}
        reloadByTabChange={reloadByTabChange}
      />
    </div>
  );
}
